/**
 * A module's task list: assessments (several per module, each uploaded from
 * the blueprint CSV) and assignment ZIPs.
 *
 * `kind` is "topic" for a 1:1 student's module (only that student sees it) or
 * "template" for a cohort batch's module (the whole batch sees it).
 */

import api from "../../api";
import { uploadFile } from "../ContentBank/contentBankApi";

const BASE = "api/module-tasks";
const at = (kind, moduleId, rest = "") => `${BASE}/${kind}/${moduleId}/${rest}`;

export { errorMessage } from "../ContentBank/contentBankApi";

const csvForm = (file, fields = {}) => {
  const form = new FormData();
  form.append("file", file);
  Object.entries(fields).forEach(([key, value]) => {
    if (value !== undefined && value !== null) form.append(key, value);
  });
  return form;
};

/** Save the blueprint CSV to the mentor's computer. */
export const downloadBlueprint = async () => {
  const { data } = await api.get(`${BASE}/blueprint/`, { responseType: "blob" });
  const url = URL.createObjectURL(data);
  const link = document.createElement("a");
  link.href = url;
  link.download = "assessment_blueprint.csv";
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

// ─── Assessments ───────────────────────────────────────────────────────────

export const listAssessments = async (kind, moduleId) =>
  (await api.get(at(kind, moduleId, "assessments/"))).data;

export const getAssessment = async (kind, moduleId, assessmentId) =>
  (await api.get(at(kind, moduleId, `assessments/${assessmentId}/`))).data;

/** Check a CSV without publishing: { total, valid, invalid_count, invalid: [{row, ref, title, type, errors}], warnings, counts }. */
export const validateCsv = async (kind, moduleId, file) =>
  (await api.post(at(kind, moduleId, "assessments/validate/"), csvForm(file))).data;

export const createAssessment = async (kind, moduleId, { file, title, description, publishValidOnly }) =>
  (await api.post(at(kind, moduleId, "assessments/"), csvForm(file, {
    title, description, publish_valid_only: publishValidOnly ? "true" : undefined,
  }))).data;

/** Rename, or (with a file) replace its questions. */
export const updateAssessment = async (kind, moduleId, assessmentId, { file, title, description, publishValidOnly }) => {
  const url = at(kind, moduleId, `assessments/${assessmentId}/`);
  if (!file) return (await api.patch(url, { title, description })).data;
  return (await api.patch(url, csvForm(file, {
    title, description, publish_valid_only: publishValidOnly ? "true" : undefined,
  }))).data;
};

export const deleteAssessment = async (kind, moduleId, assessmentId) =>
  api.delete(at(kind, moduleId, `assessments/${assessmentId}/`));

export const deleteLegacyQuestions = async (kind, moduleId) =>
  api.delete(at(kind, moduleId, "assessments/legacy/"));

// ─── Assignments ───────────────────────────────────────────────────────────

export const listAssignments = async (kind, moduleId) =>
  (await api.get(at(kind, moduleId, "assignments/"))).data;

/** Put the ZIP in storage; returns what the save calls need. */
export const uploadZip = async (kind, moduleId, file, onProgress) => {
  const { upload_url, file_key, content_type } = (await api.post(at(kind, moduleId, "assignments/upload-url/"), {
    file_name: file.name,
    content_type: file.type || "application/zip",
  })).data;
  await uploadFile(upload_url, file, content_type, onProgress);
  return { file_key, file_name: file.name, file_size: file.size };
};

export const createAssignment = async (kind, moduleId, fields) =>
  (await api.post(at(kind, moduleId, "assignments/"), fields)).data;

export const updateAssignment = async (kind, moduleId, assignmentId, fields) =>
  (await api.patch(at(kind, moduleId, `assignments/${assignmentId}/`), fields)).data;

export const deleteAssignment = async (kind, moduleId, assignmentId) =>
  api.delete(at(kind, moduleId, `assignments/${assignmentId}/`));

export const assignmentDownloadUrl = async (assignmentId) =>
  (await api.get(`api/assignment-library/${assignmentId}/download/`)).data;
